import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useInfiniteQuery } from '@tanstack/react-query';

import * as Backend from '@spacefill/shared/src/utils/Backend';
import { getWarehouseLabel } from '@spacefill/shared/src/components/warehouses/configuration/Helpers';
import { useUserContext } from '@spacefill/shared/src/utils/UserContext';
import { Divider } from '@spacefill/uikit/src/components/Divider';
import { EmptyState } from '@spacefill/uikit/src/components/EmptyState';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { SkeletonRow } from '@spacefill/uikit/src/components/Skeletons/SkeletonRow';
import { RequiredAction } from '@spacefill/uikit/src/components/TaskNotification';

import emptyTasksSVG from '../../assets/empty-states/empty-state-task.svg';
import { templates } from './RequiredActionsTemplates';

const PER_PAGE = 10;

function ActionSkeleton() {
    return (
        <div className='py-3'>
            <SkeletonRow
                rows={[['w-6', 'w-4/5', 'w-6'], 'w-full', 'w-full', 'w-1/2']}
                height='h-4'
            />
        </div>
    );
}

export function RequiredActionsList({ openTodosCount }) {
    const { user } = useUserContext();
    const { t } = useTranslation();
    const [showInitialLoading, setShowInitialLoading] = useState(true);

    const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching, isLoading } = useInfiniteQuery({
        queryKey: ['todo-tasks'],
        queryFn: ({ pageParam = 0 }) => Backend.call('todo-tasks', { limit: PER_PAGE, offset: pageParam * PER_PAGE }),
        getNextPageParam: (lastPage) => {
            return lastPage.nextPage ?? undefined; // undefined is important to have `hasNextPage` false
        },
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!isLoading && !isFetching && data?.pages?.[0]) {
            setShowInitialLoading(false);
        }
    }, [isLoading, isFetching, data]);

    const observer = useRef();
    const lastProjectElementRef = useCallback(
        (node) => {
            if (isFetchingNextPage) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new window.IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
            if (node) observer.current.observe(node);
        },
        [isFetchingNextPage, fetchNextPage, hasNextPage]
    );

    const totalCount = data?.pages?.[0]?.count;
    const loadedCount = data?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0;
    const remainingCount = totalCount - loadedCount;
    const skeletonCount = showInitialLoading
        ? Math.min(openTodosCount || 4, 4)
        : isFetching
          ? Math.min(remainingCount || 4, 4)
          : 0;

    return (
        <div>
            {!showInitialLoading && totalCount === 0 && (
                <EmptyState
                    picture={{
                        src: emptyTasksSVG,
                        width: 121,
                        height: 121,
                    }}
                    paragraph={t("All your upcoming tasks will show up here. Right now, it's all clear. Great job! ✨")}
                />
            )}
            {(data?.pages || []).map((todoTasks, pageIndex) => {
                return (
                    <Fragment key={pageIndex}>
                        {(todoTasks.data || []).map((todoTask, taskIndex) => {
                            if (!(todoTask.taskType in templates)) {
                                return null;
                            }
                            const notificationTemplate = templates[todoTask.taskType];
                            const url = notificationTemplate.url(todoTask);
                            const isLastPage = pageIndex === data?.pages?.length - 1;
                            const isLastTask = taskIndex === todoTasks?.data?.length - 1;
                            const isLastElement = isLastPage && isLastTask && !hasNextPage;

                            return url ? (
                                <Link
                                    key={`Notification-${todoTask.id}`}
                                    to={notificationTemplate.url(todoTask)}
                                >
                                    <RequiredAction
                                        chipIcon={notificationTemplate.chipIcon(todoTask)}
                                        user={todoTask.createdBy}
                                        receivedAt={new Date(todoTask.createdAt)}
                                        warehouseName={getWarehouseLabel(user, todoTask.order?.warehouse)}
                                    >
                                        {notificationTemplate.content(todoTask)}
                                    </RequiredAction>
                                    {isLastElement ? <div className='h-4' /> : <Divider />}
                                </Link>
                            ) : (
                                <Fragment key={`Notification-${todoTask.id}`}>
                                    <RequiredAction
                                        chipIcon={notificationTemplate.chipIcon(todoTask)}
                                        user={todoTask.createdBy}
                                        receivedAt={new Date(todoTask.createdAt)}
                                        warehouseName={getWarehouseLabel(user, todoTask.order?.warehouse)}
                                    >
                                        {notificationTemplate.content(todoTask)}
                                    </RequiredAction>
                                    {isLastElement ? <div className='h-4' /> : <Divider />}
                                </Fragment>
                            );
                        })}
                    </Fragment>
                );
            })}
            {(showInitialLoading || isFetching) && skeletonCount > 0 && (
                <>
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <Fragment key={index}>
                            <ActionSkeleton />
                            {index < skeletonCount - 1 && <Divider />}
                        </Fragment>
                    ))}
                </>
            )}
            {hasNextPage && (
                <div ref={lastProjectElementRef}>
                    <Loader />
                </div>
            )}
        </div>
    );
}
